import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Axios from "axios";
import LoadingSpinner from "../../utils/Loading";
import DashboardAdmin from "../DashboardAdmin";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import BusinessIcon from "@material-ui/icons/Business";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import PublicIcon from "@material-ui/icons/Public";
import LocationCityIcon from "@material-ui/icons/LocationCity";
import EditLocationIcon from "@material-ui/icons/EditLocation";
import CategoryIcon from "@material-ui/icons/Category";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import StorefrontIcon from "@material-ui/icons/Storefront";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import useUserState from "../../hooks/useUserState";
import swal from "sweetalert";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import { extractApiError } from "../../../services/apiErrorMessage";
import {
  getPharmacyCompletion,
  isPharmacyFormValid,
  isValidAgreementCode,
  normalizeAgreementCode,
} from "../../../utils/pharmacyValidation";

import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Container,
  MenuItem,
  Typography,
  InputAdornment,
  Switch,
  Tooltip,
} from "@material-ui/core";
import TablePharmacy from "../../utils/TablePharmacy";
import PlaceTypePicker from "./PlaceTypePicker";
import PlaceSummary from "./PlaceSummary";
import AgreementCodeField from "./AgreementCodeField";
import AgreementCodeDialog from "./AgreementCodeDialog";

const useStyles = makeStyles((theme) => ({
  canvas: {
    position: "relative",
    padding: theme.spacing(1, 0, 4),
    maxWidth: 1400,
    margin: "0 auto",
    minWidth: 0,
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#0077B6",
    marginBottom: theme.spacing(0.75),
  },
  title: {
    fontSize: "1.875rem",
    fontWeight: 700,
    letterSpacing: "-0.025em",
    lineHeight: 1.15,
    color: "#0f3a52",
    margin: 0,
  },
  subtitle: {
    marginTop: theme.spacing(0.75),
    maxWidth: 560,
    fontSize: "0.875rem",
    lineHeight: 1.55,
    color: "#5d7385",
  },
  countBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    borderRadius: 999,
    background: "#fff",
    border: "1px solid #e2eaf1",
    boxShadow: "0 10px 22px -18px rgba(8, 82, 121, .8)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#0f3a52",
    fontVariantNumeric: "tabular-nums",
  },
  layout: {
    display: "grid",
    // minmax(0, ...) lets the columns shrink, otherwise the content of the
    // table forces the whole page to be wider than the viewport
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: theme.spacing(3),
    alignItems: "start",
    // @material-ui/styles 4.11 does not support responsive values in
    // makeStyles, the media query has to be explicit.
    "@media (min-width: 1200px)": {
      gridTemplateColumns: "minmax(0, 1fr) 320px",
    },
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    minWidth: 0,
  },
  // the MUI grid items must be allowed to shrink, otherwise they force the
  // whole page to be wider than the viewport
  gridItem: {
    minWidth: 0,
  },
  sideColumn: {
    "@media (min-width: 1200px)": {
      position: "sticky",
      top: 24,
    },
  },
  card: {
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #e6edf3",
    boxShadow: "0 18px 38px -30px rgba(8, 82, 121, .85)",
    overflow: "hidden",
    minWidth: 0,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(2, 2.5, 0),
  },
  cardIcon: {
    display: "grid",
    placeItems: "center",
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "#eaf3fa",
    color: "#0077B6",
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: "0.9375rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    color: "#0f3a52",
  },
  cardSubtitle: {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 400,
    color: "#7b8fa0",
    marginTop: 1,
  },
  cardBody: {
    padding: theme.spacing(2, 2.5, 2.5),
  },
  actions: {
    position: "sticky",
    bottom: theme.spacing(1.5),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 16,
    background: "rgba(255, 255, 255, .92)",
    backdropFilter: "saturate(180%) blur(8px)",
    border: "1px solid #e2eaf1",
    boxShadow: "0 20px 40px -26px rgba(8, 82, 121, .9)",
  },
  actionsNote: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.8125rem",
    color: "#5d7385",
  },
  actionsNoteStrong: {
    color: "#0f3a52",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  actionsButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 14,
    border: "1px solid #e2eaf1",
    background: "#f8fbfd",
  },
  statusText: {
    fontSize: "0.8125rem",
    color: "#5d7385",
    maxWidth: 420,
  },
  tableCard: {
    marginTop: theme.spacing(3),
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #e6edf3",
    boxShadow: "0 18px 38px -30px rgba(8, 82, 121, .85)",
    overflow: "hidden",
    minWidth: 0,
  },
  // the table is wider than the screen, it scrolls by itself instead of
  // stretching the whole page
  tableScroll: {
    overflowX: "auto",
    contain: "inline-size",
    minWidth: 0,
  },
  tableHeader: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid #eef3f7",
  },
  tableTitle: {
    fontSize: "0.9375rem",
    fontWeight: 700,
    color: "#0f3a52",
  },
  tableHint: {
    fontSize: "0.75rem",
    color: "#7b8fa0",
  },
  // the contextual bar that appears when the admin selects some places
  selectionBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    margin: theme.spacing(2, 2.5, 0),
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 14,
    border: "1px solid #cfe4f2",
    background: "linear-gradient(120deg, #f2f8fd 0%, #ffffff 70%)",
  },
  selectionCount: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: "#085279",
    fontVariantNumeric: "tabular-nums",
  },
  selectionActions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#fcfdfe",
      transition: "background 160ms, box-shadow 160ms",
      "&.Mui-focused": {
        background: "#fff",
      },
    },
  },
  inputError: {
    "& .MuiOutlinedInput-root": {
      background: "#fff8f8",
    },
  },
  helper: {
    marginTop: 6,
    marginLeft: 2,
    fontSize: "0.75rem",
    lineHeight: 1.45,
    color: "#8497a6",
  },
  helperError: {
    color: "#c62828",
    fontWeight: 600,
  },
  adornment: {
    color: "#8fa6b7",
    "&.Mui-focused": {
      color: "#0077B6",
    },
  },
  fadeUp: {
    animation: "$riseIn 560ms cubic-bezier(.22,.61,.36,1) both",
  },
  "@keyframes riseIn": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "none" },
  },
}));

/**
 * A labeled input with an icon, a hint and an inline error message.
 */
function Field({
  id,
  label,
  hint,
  errorText,
  icon: Icon,
  children,
  ...inputProps
}) {
  const classes = useStyles();
  const hasError = !!errorText;

  return (
    <TextField
      id={id}
      label={label}
      variant="outlined"
      fullWidth
      className={`${classes.input} ${hasError ? classes.inputError : ""}`}
      error={hasError}
      helperText={
        <span className={`${classes.helper} ${hasError ? classes.helperError : ""}`}>
          {errorText || hint}
        </span>
      }
      InputProps={{
        startAdornment: Icon ? (
          <InputAdornment position="start">
            <Icon fontSize="small" className={classes.adornment} />
          </InputAdornment>
        ) : null,
      }}
      {...inputProps}
    >
      {children}
    </TextField>
  );
}

/**
 * A card that groups the fields of one step of the form.
 */
function Section({ icon: Icon, title, subtitle, children, delay = 0, className }) {
  const classes = useStyles();

  return (
    <section
      className={`${classes.card} ${classes.fadeUp} ${className || ""}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <header className={classes.cardHeader}>
        <span className={classes.cardIcon}>
          <Icon fontSize="small" />
        </span>
        <h2 className={classes.cardTitle}>
          {title}
          {subtitle && <span className={classes.cardSubtitle}>{subtitle}</span>}
        </h2>
      </header>
      <div className={classes.cardBody}>{children}</div>
    </section>
  );
}

export default function PharmacyForm() {
  const classes = useStyles();
  let history = useHistory();
  const userState = useUserState();

  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [directions, setDirections] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("enabled");
  const [pharmacyId, setPharmacyId] = useState(null);
  const [agreementCode, setAgreementCode] = useState("");
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [pharmacyList, setPharmacyList] = useState([]);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  // remounting the table is the only way to clean its own selection
  const [tableKey, setTableKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("assign");
  const [dialogPlaces, setDialogPlaces] = useState([]);

  const { t, changeDirectLanguage } = useBDocTranslator();

  const isEditing = !!pharmacyId;

  const formValues = useMemo(
    () => ({
      name,
      email,
      phone,
      country_id: country,
      city_id: city,
      type,
      directions,
    }),
    [name, email, phone, country, city, type, directions]
  );

  const completion = useMemo(
    () => getPharmacyCompletion(formValues),
    [formValues]
  );

  const isValid = isPharmacyFormValid(formValues);

  const fieldError = (field) => showErrors && completion.missing.includes(field);

  // the message shown under the field that is missing
  const errorText = (field) =>
    fieldError(field) ? t("This field is required") : "";

  // the agreement code is optional, but when it is filled in it has to be
  // a valid code (the API uses the very same rule)
  const agreementCodeError =
    agreementCode && !isValidAgreementCode(agreementCode)
      ? t(
          "The agreement code can only use letters, numbers, hyphens and underscores."
        )
      : "";

  const clearForm = () => {
    document.getElementById("pharmacy-form")?.reset();
    setEmail("");
    setName("");
    setDirections("");
    setCity("");
    setCountry("");
    setPhone("");
    setType("");
    setStatus("enabled");
    setPharmacyId(null);
    setAgreementCode("");
    setShowErrors(false);
  };

  const scrollToForm = () => {
    document
      .getElementById("pharmacy-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const focusFirstError = () => {
    const first = completion.missing[0];
    if (!first) {
      return;
    }
    const node = document.getElementById(`field-${first}`);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => node.focus(), 320);
    }
  };

  const handleClick = async (e) => {
    e.preventDefault();

    if (!isValid) {
      setShowErrors(true);
      focusFirstError();
      return;
    }

    setLoading(true);
    const payload = {
      email: email,
      name: name,
      directions: directions,
      city_id: city,
      phone: phone,
      country_id: country,
      type,
      // the code is always sent, null clears the one that was stored
      agreement_code: normalizeAgreementCode(agreementCode),
      ...(isEditing ? { status } : {}),
    };

    const url = isEditing
      ? `${process.env.REACT_APP_API_URL_BASE}/pharmacy/${pharmacyId}`
      : `${process.env.REACT_APP_API_URL_BASE}/pharmacy/create`;

    try {
      const response = isEditing
        ? await Axios.put(url, payload, {
            headers: { Authorization: "Bearer " + userState[0].jwt },
          })
        : await Axios.post(url, payload, {
            headers: { Authorization: "Bearer " + userState[0].jwt },
          });

      if (response.status === 200) {
        swal(
          t("Success!"),
          t(
            isEditing
              ? "Pharmacy updated successfully"
              : "Pharmacy registered successfully"
          ),
          t("success")
        );
        clearForm();
        getAllPharmacies();
      }
    } catch (error) {
      console.log(error);
      swal(
        t("Error!"),
        extractApiError(error, t("Something went wrong")),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fills the form with the data of a place
  const fillForm = (pharmacy) => {
    setPharmacyId(pharmacy.id);
    setName(pharmacy.name || "");
    setEmail(pharmacy.email || "");
    setPhone(pharmacy.phone || "");
    setDirections(pharmacy.directions || "");
    setType(pharmacy.type || "");
    setStatus(pharmacy.status || "enabled");
    setAgreementCode(normalizeAgreementCode(pharmacy.agreement_code) || "");
    setCountry(
      pharmacy.country_id !== undefined && pharmacy.country_id !== null
        ? pharmacy.country_id
        : ""
    );
    setCity(
      pharmacy.city_id !== undefined && pharmacy.city_id !== null
        ? pharmacy.city_id
        : ""
    );
    setShowErrors(false);
  };

  // Load the place that is being edited and fill the form with it
  const clickEvent = async (row) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/pharmacy/findById/${row.id}`,
        {
          headers: { Authorization: "Bearer " + userState[0].jwt },
        }
      );

      fillForm(response.data);
      scrollToForm();
    } catch (error) {
      console.log(error);
      swal(
        t("Error!"),
        extractApiError(error, t("Something went wrong")),
        "error"
      );
    }
  };

  // Reload a place that is being edited, so the form is never stale
  const refreshPlace = async (id) => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/pharmacy/findById/${id}`,
        {
          headers: { Authorization: "Bearer " + userState[0].jwt },
        }
      );

      fillForm(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  // Opens the bulk modal to assign (or remove) the code of the places
  const openAgreementDialog = (mode, places) => {
    setDialogMode(mode);
    setDialogPlaces(places);
    setDialogOpen(true);
  };

  // The shortcut of the form: the code of this place can be pushed to the
  // other places of the same franchise
  const applyAgreementToOthers = () => {
    const current = pharmacyList.find(
      (place) => String(place.id) === String(pharmacyId)
    );

    openAgreementDialog("assign", current ? [current] : []);
  };

  const clearSelection = () => {
    setSelectedPlaces([]);
    setTableKey((key) => key + 1);
  };

  // Assign (or remove) the agreement code of several places at once
  const handleAgreementCode = async (affectedPlaces, code) => {
    const ids = (affectedPlaces || [])
      .map((place) => place.id)
      .filter((id) => id !== undefined && id !== null);

    if (ids.length === 0) {
      swal(t("Error!"), t("No places selected"), "error");
      return;
    }

    try {
      const response = await Axios.put(
        `${process.env.REACT_APP_API_URL_BASE}/pharmacy/agreement-code`,
        { ids, agreement_code: code || null },
        { headers: { Authorization: "Bearer " + userState[0].jwt } }
      );

      const updated =
        response.data && typeof response.data.updated === "number"
          ? response.data.updated
          : ids.length;

      swal(
        t("Success!"),
        code
          ? t("Agreement code assigned to N places", { count: updated })
          : t("The agreement code was removed from N places", {
              count: updated,
            }),
        t("success")
      );

      setDialogOpen(false);
      clearSelection();
      getAllPharmacies();

      // the place that is being edited could have changed
      if (pharmacyId && ids.some((id) => String(id) === String(pharmacyId))) {
        await refreshPlace(pharmacyId);
      }
    } catch (error) {
      console.log(error);
      swal(
        t("Error!"),
        extractApiError(error, t("Something went wrong")),
        "error"
      );
    }
  };

  // Fetch the stored roles
  useEffect(() => {
    changeDirectLanguage("es");
    getAllPharmacies();
    Axios.get(`${process.env.REACT_APP_API_URL_BASE}/countries/findAll`, {
      headers: {
        Authorization: "Bearer " + userState[0].jwt, //the jwt is a variable which holds the token
      },
    })
      .then((res) => {
        // setRoles(res.data);
        setCountries(res.data);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    // the cities depend on the selected country
    if (!country) {
      setCities([]);
      return;
    }

    Axios.get(
      `${process.env.REACT_APP_API_URL_BASE}/countries/findCities?id=${country}`,
      {
        headers: {
          Authorization: "Bearer " + userState[0].jwt, //the jwt is a variable which holds the token
        },
      }
    )
      .then((res) => {
        // setRoles(res.data);
        setCities(res.data);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [country]);

  const getAllPharmacies = () => {
    Axios.get(
      `${process.env.REACT_APP_API_URL_BASE}/pharmacy/findAll?order=created_at&status=all`,
      {
        headers: {
          Authorization: "Bearer " + userState[0].jwt, //the jwt is a variable which holds the token
        },
      }
    )
      .then((res) => {
        setPharmacyList(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <DashboardAdmin
      sectionTitle={isEditing ? t("Edit Pharmacy") : t("Register Pharmacy")}
      description={
        isEditing
          ? t("Edit a place in the system")
          : t("Register a new pharmacy")
      }
    >
      <Container component="main" maxWidth={false}>
        <CssBaseline />
        <div className={classes.canvas}>
          <header className={classes.header}>
            <div>
              <span className={classes.eyebrow}>
                <StorefrontIcon style={{ fontSize: "0.95rem" }} />
                {t("Places")}
              </span>
              <h1 className={classes.title}>
                {isEditing
                  ? t("Edit a place in the system")
                  : t("Register a new place in the system")}
              </h1>
              <p className={classes.subtitle}>
                {isEditing
                  ? t(
                      "Update the data of the place. It is shown in the prescription screen as soon as it is saved."
                    )
                  : t(
                      "Fill the data of the place. It appears in the prescription screen of the providers as soon as it is registered."
                    )}
              </p>
            </div>
            <div className={classes.countBadge}>
              <StorefrontIcon style={{ fontSize: "1.05rem", color: "#0077B6" }} />
              {pharmacyList.length} {t("places registered")}
            </div>
          </header>

          <div className={classes.layout}>
            <div className={classes.column}>
              <Section
                icon={CategoryIcon}
                title={t("Identification")}
                subtitle={t("How the place is shown in the prescription list")}
                delay={0}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} className={classes.gridItem}>
                    <Field
                      id="field-name"
                      label={t("Name")}
                      icon={BusinessIcon}
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setShowErrors(false);
                      }}
                      errorText={errorText("name")}
                      hint={t("How the place is shown in the prescription list")}
                      autoFocus
                      name="name"
                      autoComplete="name"
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        marginBottom: 8,
                        fontSize: "0.6875rem",
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#6d8496",
                      }}
                    >
                      {t("Type")}
                    </Typography>
                    <PlaceTypePicker
                      value={type}
                      onChange={(value) => {
                        setType(value);
                        setShowErrors(false);
                      }}
                      t={t}
                    />
                    {fieldError("type") && (
                      <span
                        className={`${classes.helper} ${classes.helperError}`}
                      >
                        {t("Choose the type of the place")}
                      </span>
                    )}
                  </Grid>
                </Grid>
              </Section>

              <Section
                icon={EmailIcon}
                title={t("Contact")}
                subtitle={t("How the patients reach the place")}
                delay={70}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} className={classes.gridItem}>
                    <Field
                      id="field-email"
                      label="E-mail"
                      icon={EmailIcon}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setShowErrors(false);
                      }}
                      errorText={errorText("email")}
                      hint={t("Where the notifications are sent")}
                      name="email"
                      autoComplete="email"
                    />
                  </Grid>
                  <Grid item xs={12} md={6} className={classes.gridItem}>
                    <Field
                      id="field-phone"
                      label={t("Phone number")}
                      icon={PhoneIcon}
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        setShowErrors(false);
                      }}
                      errorText={errorText("phone")}
                      hint={t("The number the patients call")}
                      name="phone"
                      autoComplete="phone"
                    />
                  </Grid>
                </Grid>
              </Section>

              <Section
                icon={PublicIcon}
                title={t("Location")}
                subtitle={t("Where the place is located")}
                delay={140}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} className={classes.gridItem}>
                    <Field
                      id="field-country_id"
                      label={t("Country")}
                      icon={PublicIcon}
                      value={country}
                      onChange={(e) => {
                        setCountry(e.target.value);
                        // the city of the previous country does not belong here
                        setCity("");
                        setShowErrors(false);
                      }}
                      errorText={errorText("country_id")}
                      hint={t("Only the cities of the selected country are shown")}
                      name="country"
                      select
                      SelectProps={{ native: false }}
                    >
                      {countries.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={6} className={classes.gridItem}>
                    <Field
                      id="field-city_id"
                      label={t("City")}
                      icon={LocationCityIcon}
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        setShowErrors(false);
                      }}
                      errorText={errorText("city_id")}
                      hint={t("Where the place is located")}
                      name="city"
                      select
                      disabled={!country}
                      SelectProps={{ native: false }}
                    >
                      {cities.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <Field
                      id="field-directions"
                      label={t("Directions")}
                      icon={EditLocationIcon}
                      value={directions}
                      onChange={(e) => {
                        setDirections(e.target.value);
                        setShowErrors(false);
                      }}
                      errorText={errorText("directions")}
                      hint={t("Landmarks, floor or a reference to help the patient arrive")}
                      name="directions"
                      autoComplete="off"
                    />
                  </Grid>
                </Grid>
              </Section>

              {isEditing && (
                <Section
                  icon={ToggleOnIcon}
                  title={t("Status")}
                  subtitle={t("Availability of the place")}
                  delay={210}
                >
                  <div className={classes.statusBox}>
                    <div>
                      <Typography
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "#0f3a52",
                        }}
                      >
                        {status === "enabled"
                          ? t("Enabled")
                          : t("Disabled")}
                      </Typography>
                      <span className={classes.statusText}>
                        {status === "enabled"
                          ? t(
                              "The place is available in the prescription lists of the providers."
                            )
                          : t(
                              "The place stays hidden from the prescription lists until it is enabled again."
                            )}
                      </span>
                    </div>
                    <Tooltip
                      title={
                        status === "enabled"
                          ? t("Disable the place")
                          : t("Enable the place")
                      }
                    >
                      <Switch
                        checked={status === "enabled"}
                        onChange={(e) =>
                          setStatus(e.target.checked ? "enabled" : "disabled")
                        }
                        color="primary"
                        inputProps={{ "aria-label": t("Status") }}
                      />
                    </Tooltip>
                  </div>
                </Section>
              )}

              <Section
                icon={LocalOfferIcon}
                title={t("Agreement code")}
                subtitle={t("The code the patient uses to get the discount in this place")}
                delay={280}
              >
                <AgreementCodeField
                  value={agreementCode}
                  onChange={(value) => setAgreementCode(value || "")}
                  error={agreementCodeError}
                  t={t}
                  // the shortcut only makes sense on a place that already
                  // exists, there is nothing to mark while registering a new one
                  onApplyToOthers={isEditing ? applyAgreementToOthers : undefined}
                />
              </Section>

              <div className={classes.actions}>
                <span className={classes.actionsNote}>
                  {completion.missing.length === 0 ? (
                    <>
                      <CheckCircleOutlineIcon
                        style={{ fontSize: "1.1rem", color: "#21a08f" }}
                      />
                      {t("All the required fields are complete")}
                    </>
                  ) : (
                    <>
                      <CancelIcon
                        style={{ fontSize: "1.1rem", color: "#c98a2e" }}
                      />
                      <span className={classes.actionsNoteStrong}>
                        {completion.missing.length}
                      </span>
                      {t("fields are still missing")}
                    </>
                  )}
                </span>

                <div className={classes.actionsButtons}>
                  {isEditing && (
                    <Button
                      variant="text"
                      color="default"
                      onClick={(e) => {
                        e.preventDefault();
                        clearForm();
                      }}
                    >
                      {t("Discard changes")}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="default"
                    onClick={(e) => history.goBack()}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={handleClick}
                    disabled={loading}
                  >
                    {isEditing ? t("Update") : t("Save")}
                  </Button>
                </div>
              </div>

              {loading && (
                <div className="d-flex justify-content-center py-2">
                  <LoadingSpinner type="spinningBubbles" color="#1d3557" />
                </div>
              )}
            </div>

            <aside className={classes.sideColumn}>
              <div className={classes.fadeUp} style={{ animationDelay: "120ms" }}>
                <PlaceSummary
                  values={formValues}
                  countries={countries}
                  cities={cities}
                  completion={completion}
                  showErrors={showErrors}
                  t={t}
                />
              </div>
            </aside>
          </div>

          <div className={classes.tableCard}>
            <div className={classes.tableHeader}>
              <div>
                <div className={classes.tableTitle}>{t("Pharmacies")}</div>
                <div className={classes.tableHint}>
                  {t("Select the edit icon to update the data of a place")}
                </div>
              </div>
              <span className={classes.countBadge}>
                {pharmacyList.length} {t("places registered")}
              </span>
            </div>
            {selectedPlaces.length > 0 && (
              <div className={classes.selectionBar}>
                <span className={classes.selectionCount}>
                  <LocalOfferIcon style={{ fontSize: "1.05rem" }} />
                  {t("N places selected", { count: selectedPlaces.length })}
                </span>
                <div className={classes.selectionActions}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<LocalOfferIcon />}
                    onClick={() =>
                      openAgreementDialog("assign", selectedPlaces)
                    }
                  >
                    {t("Assign agreement code")}
                  </Button>
                  <Button
                    variant="text"
                    color="default"
                    onClick={() =>
                      openAgreementDialog("remove", selectedPlaces)
                    }
                  >
                    {t("Remove agreement code")}
                  </Button>
                  <Button
                    variant="text"
                    color="default"
                    onClick={clearSelection}
                  >
                    {t("Clear selection")}
                  </Button>
                </div>
              </div>
            )}
            <div className={classes.tableScroll}>
              <TablePharmacy
                key={tableKey}
                translator={t}
                data={pharmacyList}
                clickEvent={clickEvent}
                onSelectionChange={(rows) => setSelectedPlaces(rows || [])}
              />
            </div>
          </div>

          <AgreementCodeDialog
            open={dialogOpen}
            mode={dialogMode}
            places={dialogPlaces}
            allPlaces={pharmacyList}
            initialCode={agreementCode}
            t={t}
            onClose={() => setDialogOpen(false)}
            onConfirm={handleAgreementCode}
          />
        </div>
      </Container>
    </DashboardAdmin>
  );
}
